import { css } from '@emotion/react';
import { format, getYear, isSameDay, isSameMonth, startOfWeek } from 'date-fns';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, startOfMonth } from 'date-fns/esm';
import { IconArrowLeft } from 'icons/ArrowLeft';
import { IconArrowRight } from 'icons/ArrowRight';
import React, { useState, useRef, ReactNode } from 'react';
import { ButtonCircleStyle } from 'styles/ButtonCircleStyle';

import { DropdownStyle } from 'styles/DropdownStyle';
import { theme } from 'styles/theme';

import { useDropdown } from 'hooks/useDropdown';

import { MonthPicker } from './MonthPicker';
import { YearPicker } from './YearPicker';
import { FlexStyle } from 'styles/FlexStyles';
import { SelectStyle } from 'styles/SelectStyle';
import { PaddingStyle } from 'styles/PaddingStyle';
import { Button } from './Button';

export const DatePicker = ({
	children,
	onChange,
	renderTrigger,
	value,
}: {
	children?: any;
	onChange: (date: Date, toggle: () => void) => void;
	renderTrigger: ({ isOpen }: { isOpen?: boolean }) => ReactNode;
	value: string | Date;
}) => {
	// const { date } = useParamDate({});

	const date = typeof value === 'string' ? new Date(value) : value ? value : new Date();

	const today = new Date();

	const dropdownRef = useRef<HTMLDivElement>(null);
	const [open, toggleDropdown] = useDropdown(dropdownRef);

	const [currentDate, setCurrentDate] = useState(date);

	const monthStart = startOfMonth(currentDate);
	const monthEnd = endOfMonth(currentDate);

	const prevMonth = addMonths(monthStart, -1);
	const nextMonth = addMonths(monthStart, 1);

	const weekStart = startOfWeek(monthStart, { weekStartsOn: 1 });
	const weekEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

	const interval = eachDayOfInterval({
		start: weekStart,
		end: weekEnd,
	});

	const chunkArray = (arr: Date[], size: number): Date[][] =>
		arr.length > size ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)] : [arr];

	const intervalChunk = chunkArray(interval, 7);

	const trigger = renderTrigger?.({ isOpen: open });

	return (
		<div
			ref={dropdownRef}
			css={css`
				position: relative;
				font-weight: normal;
			`}
		>
			<div
				onClick={() => {
					setCurrentDate(date);
					toggleDropdown();
				}}
			>
				{trigger}
				{children}
			</div>
			{!!open && (
				<div
					css={[
						DropdownStyle(),
						css`
							position: absolute;
							top: 100%;
							z-index: 10;
							padding: 20px;
							width: 320px;
							max-height: none;
							overflow: visible;
						`,
					]}
				>
					<div
						css={[
							FlexStyle({ alignItemsCenter: true, justifyContentBetween: true }),
							css`
								margin-bottom: 12px;
							`,
						]}
					>
						<div
							css={css`
								font-size: 16px;
								line-height: 20px;
								font-weight: 600;
								display: flex;
							`}
						>
							<MonthPicker
								handleClick={(month, _dropdownToggle) => {
									const nextDate = format(currentDate, 'yyyy-') + (month + 1) + '-' + '01'; // tohle nebude fungovat kdyz bude mesic 12 => 12 + 1 => 13
									setCurrentDate(() => new Date(nextDate));
									//dropdownToggle();
								}}
								date={currentDate}
								renderTrigger={({ isOpen }) => (
									<div css={DatePickerSelectStyle()} className={isOpen ? 'open' : ''}>
										{format(currentDate, 'MMMM')}
									</div>
								)}
							/>

							<YearPicker
								from={2021}
								to={getYear(new Date())}
								date={currentDate}
								handleClick={(year, dropdownToggle) => {
									const nextDate = year + '-01-01';
									setCurrentDate(() => new Date(nextDate));
									dropdownToggle();
								}}
								renderTrigger={({ isOpen }) => (
									<div css={DatePickerSelectStyle()} className={isOpen ? 'open' : ''}>
										{format(currentDate, 'YYY')}
									</div>
								)}
							/>
						</div>

						<div css={FlexStyle()}>
							<button
								type="button"
								css={ButtonCircleStyle({ sizeSmall: true })}
								onClick={() => {
									setCurrentDate(prevMonth);
								}}
							>
								<IconArrowLeft />
							</button>
							<button
								type="button"
								css={ButtonCircleStyle({ sizeSmall: true })}
								onClick={() => {
									setCurrentDate(nextMonth);
								}}
							>
								<IconArrowRight />
							</button>
						</div>
					</div>
					<div css={FlexStyle({ wrap: true })}>
						<div css={FlexStyle()}>
							<div css={CalendarHeaderStyle()}>Mo</div>
							<div css={CalendarHeaderStyle()}>Tu</div>
							<div css={CalendarHeaderStyle()}>We</div>
							<div css={CalendarHeaderStyle()}>Th</div>
							<div css={CalendarHeaderStyle()}>Fr</div>
							<div css={CalendarHeaderStyle()}>Sa</div>
							<div css={CalendarHeaderStyle()}>Su</div>
						</div>
						{intervalChunk.map((currentInterval: any, index: number) => {
							return (
								<div key={index} css={FlexStyle()}>
									{currentInterval.map((item: any, index: number) => {
										return (
											<div
												key={index}
												onClick={() => {
													onChange(new Date(item), toggleDropdown);
												}}
												css={[
													css`
														width: 36px;
														height: 36px;
														text-align: center;
														margin: 2px;
														line-height: 36px;
														border-radius: 36px;
														font-size: 13px;

														color: ${theme.color.text['400']};

														&:hover {
															cursor: pointer;
															color: ${theme.color.primary['400']};
															background: ${theme.color.primary['50']};
															text-decoration: none;
														}

														${!isSameMonth(item, currentDate) &&
														css`
															color: #acadbe;
														`};

														${isSameDay(item, today) &&
														css`
															color: ${theme.color.primary['400']};
															font-weight: bold;
														`}

														${isSameDay(item, date) &&
														css`
															&,
															&:hover {
																color: ${theme.color.primary['400']};
																background: #fafaff;
																border: 1px solid ${theme.color.primary['400']};
															}
														`}
													`,
												]}
											>
												{format(item, 'd')}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>

					<div
						css={css`
							padding-top: 12px;
						`}
					>
						<Button
							small
							outline
							block
							onClick={() => {
								onChange(today, toggleDropdown);
								//setCurrentDate(today);
							}}
						>
							Today
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

const CalendarHeaderStyle = () => css`
	width: 36px;
	height: 36px;
	text-align: center;
	margin: 2px;
	font-size: 13px;
	font-weight: 500;
	color: ${theme.color.text['300']};
	line-height: 36px;
`;

const DatePickerSelectStyle = () => css`
	${SelectStyle({ ghost: true, sizeMedium: true })};
	${PaddingStyle({ l: 12, r: 28 })}

	&:after {
		right: 12px;
	}
`;
